// src/components/searchBars/SearchBar.jsx

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

import { fetchGlobalSearchResults, setCurrentQuery } from '../../redux/searchBar/searchActions';
import { setCurrentSymbol } from '../../redux/stockData/nasdaq/stockDataReducer';
import { clearGlobalSearchFocus } from '../../redux/ui/uiSlice';
import SuggestionList from './SuggestionList';


// --- Helpers ---

const normalizeQuery = (raw = '') => decodeURIComponent(String(raw)).trim();

const SearchBar = ({
  autoFocus = false,
  className = "",
  isOverlay = false,
  onSelect = () => {},
  onResults = () => {},
  onSuggestionClickRef,    // NEW: Callback to expose navigation handler
}) => {
  const { t } = useTranslation();
  const navigateRef = useRef(null);
  const [query, setQuery] = useState('');
  const [searchTag, setSearchTag] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchBarRef = useRef(null);
  const shouldFocusGlobalSearch = useSelector(
    (s) => s.ui?.focusGlobalSearch
  );

  const stocks = useSelector((s) => s.search.stocks || []);
  const users = useSelector((s) => s.search.users || []);
  const isSearchLoading = useSelector((s) => s.search.loading || false);
  const isUserQuery = query.trim().startsWith("@");

  const suggestions = useMemo(() => {
    if (isUserQuery) {
      return users.slice(0, 8);
    }
    return [...stocks, ...users].slice(0, 8);
  }, [stocks, users, isUserQuery]);

  // Feed results upward for MobileSearchOverlay
  useEffect(() => {
    if (isOverlay) onResults(suggestions);
  }, [suggestions, isOverlay, onResults]);

  // API lookup debounce
  const debouncedFetch = useCallback(
    debounce((input) => {
      const q = normalizeQuery(input);
      if (q !== '') dispatch(fetchGlobalSearchResults(q));
      else dispatch({ type: 'search/clearResults' });
    }, 250),
    [dispatch]
  );

  useEffect(() => {
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [query, debouncedFetch]);

  // Click outside to close (desktop only)
  useEffect(() => {
    const handler = (e) => {
      if (isOverlay || document.body.classList.contains('mobile-search-open')) return;
      if (searchBarRef.current && !searchBarRef.current.contains(e.target)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOverlay]);

  // Escape closes
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setIsFocused(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // --------------------------------------------------
  // Redux UI intent: focus global search (Add Ticker)
  // --------------------------------------------------
  useEffect(() => {
    if (!shouldFocusGlobalSearch) return;

    // Open dropdown / overlay logic
    setIsFocused(true);

    // Focus input safely on next paint
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    // One-shot intent: clear immediately
    dispatch(clearGlobalSearchFocus());
  }, [shouldFocusGlobalSearch, dispatch]);


  // --- Stable Navigation Function (always defined immediately) ---

  navigateRef.current = (item) => {
    //console.log('[SearchBar] navigateRef.current CALLED', item, { isOverlay });

    if (!item) return;

    if (item.symbol) {
      const sym = item.symbol.toUpperCase();

      dispatch(setCurrentSymbol(sym));
      dispatch({ type: 'stockData/setCurrentStock', payload: item });
      dispatch(setCurrentQuery(sym));
      setSearchTag(sym);

      navigate(`/stocks/${sym}`);
    } else if (item.username) {
      const h = item.username.replace(/^@+/, '');
      dispatch(setCurrentQuery(`@${h}`));
      setSearchTag(`@${h}`);
      navigate(`/public-profiles/${h}`);
    }

    if (isOverlay) onSelect(item);
  };


  
  // Expose navigation handler to parent immediately on every render (production-safe)
  useEffect(() => {
    if (!onSuggestionClickRef) return;

    onSuggestionClickRef(() => (item) => {
      navigateRef.current(item);
    });

  }, [onSuggestionClickRef]);






  // --- Direct suggestion click from desktop dropdown ---

  const handleSuggestionClick = (item) => {
    // For desktop, close dropdown immediately
    if (!isOverlay) {
      onSelect(item);
    }

    setIsFocused(false);
    setQuery("");

    // Perform navigation
    setTimeout(() => navigateRef.current(item), 0);

  };

  // Submit search (Enter key or Search button)
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    if (query.startsWith("@")) {
      navigateRef.current({ username: query.slice(1) });
    } else {
      navigateRef.current({ symbol: query });
    }

    setQuery('');
    setIsFocused(false);

    if (isOverlay) onSelect(null);
  };



  const clearInput = () => {
    setQuery('');
    setIsFocused(true);
  };

  const removeTag = () => {
    setSearchTag('');
    dispatch(setCurrentQuery(''));
    dispatch(setCurrentSymbol(''));
  };

  // 2026-09-11: searchTag only ever got *set* (on navigating to a stock via
  // this search bar) -- SearchBar lives in the app shell and never
  // remounts on route change, so nothing ever cleared it once the user
  // left the stock page some other way (sidebar nav, back button, a link).
  // The ticker chip then sat in the search bar indefinitely, stale.
  useEffect(() => {
    if (!searchTag || searchTag.startsWith('@')) return;
    if (!location.pathname.startsWith(`/stocks/${searchTag}`)) {
      setSearchTag('');
    }
  }, [location.pathname, searchTag]);

  // Show the dropdown as soon as the search is focused, not just once
  // there are results -- an empty/focused search with nothing rendered
  // gives the user no indication of what to do next. Mirrors the same
  // "start typing…" leading prompt MobileSearchOverlay already shows.
  const showDesktopDropdown = !isOverlay && isFocused;

  return (
    <div
      ref={searchBarRef}
      className={`
        ${isOverlay ? "relative w-full min-w-0 z-[100002]" : "relative w-full min-w-0"}
        bg-gray-100/80 dark:bg-gray-900/40 backdrop-blur-xl
        border border-gray-300 dark:border-gray-700 rounded-xl
        flex items-center
        px-3 py-2
        shadow-lg
        overflow-visible
        ${className}
      `}
    >
      <form onSubmit={handleSubmit} autoComplete="off" className="flex items-center w-full gap-3 min-w-0">

        {searchTag && (
          <div className="flex-shrink-0 flex items-center px-2 py-1 h-9 bg-blue-600/80 text-white text-sm rounded-lg">
            <span className="truncate max-w-[80px]">{searchTag}</span>
            <button type="button" onClick={removeTag} className="ml-2 hover:text-red-300">
              <X size={14} />
            </button>
          </div>
        )}

        <div className="relative flex-1 min-w-0 flex items-center">
          <input
            ref={inputRef}
            id="global-search-input"
            type="text"
            autoComplete="off"
            placeholder={t('nav.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            className="flex-1 min-w-0 text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 bg-transparent outline-none h-9 pr-8"
          />



          {query && (
            <button type="button" onClick={clearInput} className="absolute right-0 p-1 hover:bg-white/10 rounded-full">
              <X size={16} className="text-gray-400" />
            </button>
          )}
        </div>

        <button type="submit" className="h-9 px-4 bg-violet-800 hover:bg-violet-700 text-white text-sm font-semibold rounded-md">
          {t('nav.search')}
        </button>
      </form>

      {showDesktopDropdown && (
        suggestions.length > 0 ? (
          <SuggestionList
            suggestions={suggestions}
            onSuggestionClick={handleSuggestionClick}
            isOverlay={false}
            onSelect={onSelect}
          />
        ) : (
          <div className="absolute top-full left-0 w-full z-[100000] mt-1 rounded-lg rounded-t-none bg-white dark:bg-gray-900/95 backdrop-blur-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 text-center">
            {query.trim() ? (
              isSearchLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('nav.searching')}</p>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('nav.noMatches', { query: query.trim() })}
                </p>
              )
            ) : (
              <>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                  {t('nav.searchPrompt')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {t('nav.searchExamples')}
                </p>
              </>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default SearchBar